import React from 'react'
import { RotateCcw } from 'lucide-react'
import { Card } from './Field'
import { EmptyRow, TableWrap, Td, Th } from './Table'
import { Badge } from './Badges'
import {
	CASHBACK_STATUS_META,
	CASHBACK_TYPE_META,
	findEntryReconciliation,
} from '../cashback'
import { dash, formatDateTime, formatMoney } from '../utils'

const COLSPAN = 6

const TypeBadge = ({ type }) => {
	const meta = CASHBACK_TYPE_META[type]
	return meta ? (
		<Badge className={meta.className}>{meta.label}</Badge>
	) : (
		<span className="text-slate-400">{dash(type)}</span>
	)
}

const StatusBadge = ({ status }) => {
	const meta = CASHBACK_STATUS_META[status]
	return (
		<Badge className={meta?.className || 'bg-slate-100 text-slate-600'}>
			{meta?.label || dash(status)}
		</Badge>
	)
}


const ReconciliationCell = ({ entry, docEntry, cashback }) => {
	if (entry?.type !== 'REDEEM') return <span className="text-slate-300">—</span>
	if (cashback.reconciliationsLoading)
		return <span className="text-slate-400">Yuklanmoqda…</span>

	const recon = findEntryReconciliation({
		entry,
		docEntry,
		reconciliations: cashback.reconciliations,
		reconciliationLines: cashback.reconciliationLines,
	})

	if (!recon)
		return (
			<div>
				<Badge className="bg-amber-100 text-amber-700">
					SAP da sverka yo‘q
				</Badge>
				{entry?.notes && (
					<p className="mt-1 max-w-[220px] text-[11px] text-slate-400">
						{entry.notes}
					</p>
				)}
			</div>
		)

	return (
		<div>
			<Badge className="bg-emerald-100 text-emerald-700">
				{`Sverka №${dash(recon.invoiceLine?.reconNum)}`}
			</Badge>
			<p className="mt-1 whitespace-nowrap text-[11px] text-slate-400">
				{`${formatDateTime(recon.invoiceLine?.reconDate)} · ${formatMoney(
					recon.invoiceLine?.reconciledAmount,
				)}`}
			</p>
		</div>
	)
}

const CashbackCard = ({ docEntry, cashback, className = '' }) => {
	const { entries, earned, redeemed, redeemedTotal, currency, loading } =
		cashback

	const summary = loading ? null : redeemed.length ? (
		<Badge className="bg-emerald-100 text-emerald-700">
			{`Yechilgan: ${formatMoney(redeemedTotal)} ${currency}`.trim()}
		</Badge>
	) : (
		<Badge className="bg-slate-100 text-slate-600">Kesh-bek ishlatilmagan</Badge>
	)

	return (
		<Card
			title="Kesh-bek"
			subtitle="Shu savdo bo‘yicha hisoblangan va yechilgan kesh-bek (leder: saleId = docEntry)"
			className={className}
			right={
				<div className="flex items-center gap-2">
					{summary}
					<button
						type="button"
						onClick={cashback.reload}
						className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition hover:border-slate-300 hover:text-slate-700"
						title="Yangilash"
					>
						<RotateCcw size={14} />
					</button>
				</div>
			}
		>
			<TableWrap minWidth="min-w-[820px]">
				<thead>
					<tr>
						<Th>Sana</Th>
						<Th>Turi</Th>
						<Th align="right">Summa</Th>
						<Th>Holati</Th>
						<Th>Provodka</Th>
						<Th>SAP sverkasi</Th>
					</tr>
				</thead>
				<tbody>
					{loading && <EmptyRow colSpan={COLSPAN} text="Yuklanmoqda…" />}
					{!loading && entries.length === 0 && (
						<EmptyRow
							colSpan={COLSPAN}
							text="Bu savdo bo‘yicha kesh-bek hisoblanmagan va yechilmagan"
						/>
					)}
					{!loading &&
						entries.map((entry, index) => (
							<tr key={entry?.id ?? index} className="align-top">
								<Td className="whitespace-nowrap">
									{formatDateTime(entry?.createdAt)}
								</Td>
								<Td>
									<TypeBadge type={entry?.type} />
								</Td>
								<Td className="whitespace-nowrap text-right font-medium">
									{`${formatMoney(entry?.amount)} ${entry?.currency || ''}`.trim()}
								</Td>
								<Td>
									<StatusBadge status={entry?.status} />
									{entry?.type !== 'REDEEM' && entry?.usedAt && (
										<p className="mt-1 whitespace-nowrap text-[11px] text-slate-400">
											{`ishlatilgan ${formatDateTime(entry.usedAt)}`}
										</p>
									)}
								</Td>
								<Td className="font-mono text-[13px] text-slate-600">
									{dash(entry?.journalEntryNumber)}
								</Td>
								<Td>
									<ReconciliationCell
										entry={entry}
										docEntry={docEntry}
										cashback={cashback}
									/>
								</Td>
							</tr>
						))}
				</tbody>
			</TableWrap>

			{!loading && redeemed.length === 0 && earned.length > 0 && (
				<p className="mt-3 text-[12px] text-slate-400">
					{earned.some((entry) =>
						['AVAILABLE', 'ACTIVE'].includes(entry?.status),
					)
						? 'Kesh-bek shu savdo bo‘yicha hisoblangan va hali ishlatilmagan'
						: 'Kesh-bek shu savdo bo‘yicha hisoblangan va boshqa savdoda ishlatilgan'}
				</p>
			)}
		</Card>
	)
}

export default CashbackCard
